import { useEffect, type ReactNode } from "react";

export interface ModalProps {
  /** Whether the modal is shown. */
  open: boolean;
  /** Dialog heading. */
  title: string;
  /** Close request (overlay click, Esc, or the × button). */
  onClose: () => void;
  /** Body content. */
  children: ReactNode;
  /** Optional action row pinned to the bottom of the dialog. */
  footer?: ReactNode;
  /** Dialog width. "default" is the form-sized dialog; "wide" suits a filter row over a data grid,
   *  which a form-width dialog would force into wrapping; "full" is the near-full-height dialog a
   *  whole edit screen lives in (the product detail), which scrolls inside itself. Omit for the
   *  default (render unchanged). */
  size?: "default" | "wide" | "full";
  /** Optioneel: een icoon in een rond vlak links van de titel. Zonder blijft de kop zoals hij was. */
  icon?: ReactNode;
  /** Optioneel: een regel uitleg onder de titel. */
  subtitle?: string;
}

/**
 * jve-6-5: a centred modal dialog — dimmed overlay + a surface card with a titled header (× close)
 * and an optional footer action row. Closes on overlay click and Escape. Presentational +
 * extraction-ready; mirrors the Jmix dialog vocabulary (add/edit, delete-with-usage).
 */
export function Modal({ open, title, onClose, children, footer, size = "default", icon, subtitle }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fui-modal-overlay" onClick={onClose}>
      <div
        className={size === "default" ? "fui-modal" : `fui-modal fui-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={icon || subtitle ? "fui-modal__header fui-modal__header--rijk" : "fui-modal__header"}>
          {icon || subtitle ? (
            <div className="fui-modal__kop">
              {icon && <span className="fui-modal__icoon" aria-hidden="true">{icon}</span>}
              <div className="fui-modal__koptekst">
                <h2 className="fui-modal__title">{title}</h2>
                {subtitle && <p className="fui-modal__subtitle">{subtitle}</p>}
              </div>
            </div>
          ) : (
            <h2 className="fui-modal__title">{title}</h2>
          )}
          <button type="button" className="fui-modal__close" aria-label="Sluiten" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="fui-modal__body">{children}</div>
        {footer && <div className="fui-modal__footer">{footer}</div>}
      </div>
    </div>
  );
}
